/**
 * bgRemover.js — AI Background Removal via Transformers.js
 * Model: briaai/RMBG-1.4 (q8 quantised, ~43 MB, cached after first load)
 * Pipeline: AutoModel + AutoProcessor → alpha mask → canvas composite
 */

import { AutoModel, AutoProcessor, env, RawImage } from "@huggingface/transformers";

// Always fetch from Hugging Face Hub; use browser cache after first load
env.allowLocalModels  = false;
env.useBrowserCache   = true;

const MODEL_ID = "briaai/RMBG-1.4";
let _model     = null;
let _processor = null;
let _loading   = null;   // shared promise so parallel calls don't double-load

// ── Model loading ─────────────────────────────────────────────────────────────

export async function loadBgModel(onProgress) {
  if (_model && _processor) return;
  if (_loading) return _loading;

  _loading = (async () => {
    const cb = (p) => {
      if (typeof onProgress !== "function") return;
      // v4 emits aggregated "progress_total"; individual files emit "progress"
      if (p?.status === "progress_total") {
        onProgress(Math.round(p.progress ?? 0));
      } else if (p?.status === "progress" && p?.total > 0) {
        onProgress(Math.round((p.loaded / p.total) * 100));
      }
    };

    [_model, _processor] = await Promise.all([
      AutoModel.from_pretrained(MODEL_ID, {
        dtype: "q8",
        config: { model_type: "custom" },
        progress_callback: cb,
      }),
      AutoProcessor.from_pretrained(MODEL_ID),
    ]);
    _loading = null;
  })();

  return _loading;
}

export function isBgModelLoaded() {
  return Boolean(_model && _processor);
}

// ── Core removal → returns canvas with transparent background ─────────────────

export async function removeBg(blob) {
  if (!_model || !_processor) throw new Error("Model not loaded");

  const url   = URL.createObjectURL(blob);
  const image = await RawImage.fromURL(url);
  URL.revokeObjectURL(url);

  const { pixel_values } = await _processor(image);
  const { output }       = await _model({ input: pixel_values });

  // Resize alpha mask to original dimensions
  const mask = await RawImage
    .fromTensor(output[0].mul(255).to("uint8"))
    .resize(image.width, image.height);

  // Draw original image then apply mask as alpha channel
  const canvas = document.createElement("canvas");
  canvas.width  = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext("2d", { alpha: true });

  // Reconstruct image from RawImage data
  const imgData = ctx.createImageData(image.width, image.height);
  for (let i = 0; i < image.width * image.height; i++) {
    imgData.data[i * 4]     = image.data[i * image.channels];
    imgData.data[i * 4 + 1] = image.data[i * image.channels + 1];
    imgData.data[i * 4 + 2] = image.data[i * image.channels + 2];
    imgData.data[i * 4 + 3] = mask.data[i]; // alpha from AI mask
  }
  ctx.putImageData(imgData, 0, 0);
  return canvas;  // transparent-bg canvas
}

// ── Background compositing helpers ────────────────────────────────────────────

export const BG_PRESETS = [
  { id: "transparent", label: "None",     value: null },
  { id: "white",       label: "White",    value: "#ffffff" },
  { id: "black",       label: "Black",    value: "#000000" },
  { id: "softgrey",   label: "Grey",     value: "#1a1a2e" },
  { id: "grad-studio",label: "Studio",   value: "linear-gradient(135deg,#0d0d0d,#2c2c2c)" },
  { id: "grad-dusk",  label: "Dusk",     value: "linear-gradient(135deg,#1a1a2e,#16213e,#0f3460)" },
  { id: "grad-sunset",label: "Sunset",   value: "linear-gradient(135deg,#f97316,#ec4899,#8b5cf6)" },
  { id: "grad-aurora",label: "Aurora",   value: "linear-gradient(135deg,#042f2e,#134e4a,#0891b2)" },
  { id: "grad-gold",  label: "Gold",     value: "linear-gradient(135deg,#451a03,#92400e,#f59e0b)" },
  { id: "blur",        label: "Blur BG",  value: "blur" },
];

/**
 * Composite a transparent-bg canvas over a background.
 * @param {HTMLCanvasElement} fgCanvas  - result of removeBg()
 * @param {Blob}              origBlob  - original photo (used for blur bg)
 * @param {string|null}       bgValue   - null=transparent, "#hex", gradient string, or "blur"
 * @param {number}            blurPx    - blur radius when bgValue === "blur"
 * @returns {Promise<Blob>} PNG blob
 */
export async function compositeBg(fgCanvas, origBlob, bgValue, blurPx = 18) {
  const W = fgCanvas.width, H = fgCanvas.height;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d", { alpha: true });

  if (bgValue === null) {
    // Transparent — just export the fg canvas as-is
    ctx.drawImage(fgCanvas, 0, 0);
    return canvasToBlob(canvas, "image/png", 0.95);
  }

  if (bgValue === "blur") {
    // Blur original image as background
    const blurImg = await blobToImage(origBlob);
    ctx.filter = `blur(${blurPx}px)`;
    ctx.drawImage(blurImg, 0, 0, W, H);
    ctx.filter = "none";
    ctx.drawImage(fgCanvas, 0, 0);
    return canvasToBlob(canvas, "image/png", 0.95);
  }

  if (bgValue.startsWith("linear-gradient")) {
    // Parse two gradient stops and fill
    const stops = bgValue.match(/#[0-9a-f]{6}/gi) || ["#000", "#000"];
    const grad  = ctx.createLinearGradient(0, 0, W, H);
    stops.forEach((c, i) => grad.addColorStop(i / Math.max(stops.length - 1, 1), c));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
  } else {
    // Solid colour
    ctx.fillStyle = bgValue;
    ctx.fillRect(0, 0, W, H);
  }

  ctx.drawImage(fgCanvas, 0, 0);
  return canvasToBlob(canvas, "image/png", 0.95);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function canvasToBlob(canvas, type = "image/png", quality = 0.95) {
  return new Promise((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob failed"))), type, quality)
  );
}

function blobToImage(blob) {
  return new Promise((res, rej) => {
    const img = new Image();
    const u   = URL.createObjectURL(blob);
    img.onload  = () => { URL.revokeObjectURL(u); res(img); };
    img.onerror = () => { URL.revokeObjectURL(u); rej(new Error("Image load failed")); };
    img.src = u;
  });
}
